/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { STATIC_RENDER_GLOBAL } from '@amzn/prerender-contract'

/**
 * In-app static-render mode flag. `true` while the app is booting inside the
 * static prerenderer (a headless render with no interactive user and no
 * browser media machinery); `false` in every normal browser session.
 *
 * The app branches on this to stub out browser-only behavior that a render
 * must never touch (playback / MSE / EME — see `appDependencies.ts`). Kept as
 * a plain module-level boolean the app reads directly (`import { staticPage }
 * from '@/staticRender'`), so app code has NO dependency on the render engine
 * or `@amzn/prerender-contract`'s runtime hooks — only this one seam.
 *
 * The render script runs the real built app as a Node module against a mock
 * DOM (see `@amzn/static-prerender`'s `renderRoute`). Because the app is
 * bundled, the script can't call {@link setStaticPage} on the app's own copy
 * of this module — so the value is SEEDED at module-eval from the boot-time
 * global (`globalThis[STATIC_RENDER_GLOBAL]`) the render sets before importing
 * the app. Served prerendered HTML never sets it, so a visitor's browser boots
 * the live app.
 */
export let staticPage = readBootFlag()

/**
 * Read the boot-time static-render global. Set to `true` by the render (the Node
 * `globalThis` assignment) BEFORE the app module
 * evaluates, so {@link staticPage} is already `true` on the app's first line
 * when rendering.
 */
function readBootFlag(): boolean {
    return (globalThis as unknown as Record<string, unknown>)[STATIC_RENDER_GLOBAL] === true
}

/**
 * Override the {@link staticPage} flag. Mainly for tests and for a render host
 * that shares this module instance with the app (unbundled). The seed above
 * already covers the normal bundled-app render path.
 */
export function setStaticPage(value: boolean): void {
    staticPage = value
}
